// components/Dashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCountries, getDestinations, getActivities } from '../api/travel.service';
import { getClientCommission, createBookingAndSendQuotation, updateQuotationAndSendEmail } from '../api/booking.service';
import { 
  Search, Loader2, Info, Globe, 
  Users, User, ShoppingCart, 
  Send, X, ChevronLeft, Trash2,
  Mail, LogOut, Menu
} from 'lucide-react';
import * as Icons from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // User data from login
  const [user, setUser] = useState(null);
  const [clientCommission, setClientCommission] = useState(0);
  const [menuItems, setMenuItems] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Page state
  const [currentPage, setCurrentPage] = useState('countries');
  
  // Master data
  const [countries, setCountries] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [allActivities, setAllActivities] = useState({});
  const [currentDestinationActivities, setCurrentDestinationActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  // User inputs
  const [totalPax, setTotalPax] = useState(1);
  const [childCount, setChildCount] = useState(0);
  const [guestName, setGuestName] = useState('');
  const [email, setEmail] = useState('');
  const [checkInDate, setCheckInDate] = useState('');

  // Cart
  const [cart, setCart] = useState([]);
  const [cartSelections, setCartSelections] = useState({});
  
  // UI States
  const [loading, setLoading] = useState({
    countries: false,
    destinations: false,
    activities: false,
    commission: false
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [quotationData, setQuotationData] = useState(null);
  const [modalEmail, setModalEmail] = useState('');
  const [agentProfit, setAgentProfit] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editBookingId, setEditBookingId] = useState(null);
  const [editItems, setEditItems] = useState([]);
  
  // Flags to prevent multiple loads
  const editLoadedRef = useRef(false);
  const cartSetRef = useRef(false);
  const destinationSetRef = useRef(false);

  // Icon mapping for menu
  const iconMap = {
    'dashboard': Icons.LayoutDashboard,
    'client': Icons.Users,
    'stats': Icons.BarChart,
    'bulkupload': Icons.Upload,
    'assignment': Icons.UserPlus,
    'default': Icons.Circle
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName?.toLowerCase()] || Icons.Circle;
    return <IconComponent size={18} />;
  };

  // Load menu items
  useEffect(() => {
    const storedMenu = localStorage.getItem('nav_list');
    if (storedMenu) {
      try {
        setMenuItems(JSON.parse(storedMenu));
      } catch (err) {
        console.error('Error parsing menu:', err);
      }
    } else {
      const defaultMenu = [
        { id: 'dashboard', title: 'Dashboard', url: '/dashboard', icon: 'dashboard' },
        { id: 'stats', title: 'Statistics', url: '/stats', icon: 'stats' }
      ];
      setMenuItems(defaultMenu);
    }
  }, []);

  // Helper to load destinations for a country
const loadDestinationsForCountry = async (countryId) => {
  setLoading(prev => ({ ...prev, destinations: true }));
  try {
    const res = await getDestinations(countryId);
    setDestinations(res.data || []);
    console.log(`✅ Loaded ${res.data?.length || 0} destinations for country ${countryId}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching destinations:", err);
    return [];
  } finally {
    setLoading(prev => ({ ...prev, destinations: false }));
  }
};

  // 🔥 EDIT MODE HANDLER - Fix destination selection
 // 🔥 EDIT MODE HANDLER - Replace this entire useEffect in your Dashboard.jsx

// 🔥 COMPLETE EDIT MODE HANDLER - With Country Loading
// components/Dashboard.jsx - Complete Working Edit Mode
// Add this useEffect for edit mode (replace the existing one)

// Dashboard.jsx - Complete edit mode with multiple destinations support

useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const isEdit = urlParams.get('edit') === 'true';
  const editDataStr = localStorage.getItem('edit_quotation');
  
  console.log('🔍 Edit mode check:', { isEdit, hasEditData: !!editDataStr });
  
  if (isEdit && editDataStr && !editLoadedRef.current) {
    editLoadedRef.current = true;
    cartSetRef.current = false;
    
    try {
      const editData = JSON.parse(editDataStr);
      console.log('📦 Edit Data:', editData);
      console.log('📦 Items with destinations:', editData.items.map(i => ({ 
        dest_id: i.destination_id, 
        desc: i.description?.substring(0, 40) 
      })));
      
      setIsEditMode(true);
      setEditBookingId(editData.id);
      setEditItems(editData.items || []);
      setCurrentPage('booking');
      
      // Set form fields
      setGuestName(editData.guest_name || '');
      setEmail(editData.email || '');
      setCheckInDate(editData.check_in_date || '');
      setTotalPax(editData.total_pax || 1);
      setChildCount(editData.children_count || 0);
      
      if (editData.items && editData.items.length > 0) {
        // Get unique destination IDs from all items
        const uniqueDestIds = [...new Set(editData.items.map(item => item.destination_id))];
        console.log(`🎯 Unique destinations: ${uniqueDestIds.length} -`, uniqueDestIds);
        
        // Set selected country (use first item's country)
        if (editData.country_id) {
          setSelectedCountry({ 
            id: editData.country_id, 
            name: editData.country_name || 'Thailand' 
          });
          loadDestinations(editData.country_id);
        }
        
        // Load activities for ALL destinations
        const loadAllDestinationsActivities = async () => {
          try {
            setLoading(prev => ({ ...prev, activities: true }));
            
            // Create a map to store activities for each destination
            const allActivitiesMap = {};
            let totalActivities = 0;
            
            for (const destId of uniqueDestIds) {
              console.log(`📡 Loading activities for destination ${destId}...`);
              const res = await getActivities(destId);
              if (res && res.data) {
                allActivitiesMap[destId] = res.data;
                totalActivities += res.data.length;
                console.log(`✅ Loaded ${res.data.length} activities for destination ${destId}`);
                
                // Cache activities
                setAllActivities(prev => ({
                  ...prev,
                  [destId]: res.data
                }));
              }
            }
            
            console.log(`✅ Total activities loaded: ${totalActivities}`);
            
            // Set current destination activities to first destination's activities
            const firstDestId = uniqueDestIds[0];
            if (allActivitiesMap[firstDestId]) {
              setCurrentDestinationActivities(allActivitiesMap[firstDestId]);
              setFilteredActivities(allActivitiesMap[firstDestId]);
            }
            
            // Set selected destination to first destination
            const firstItem = editData.items[0];
            setSelectedDestination({ 
              id: firstItem.destination_id, 
              destination_name: firstItem.destination_name || 'Destination' 
            });
            
            // Now match all activities from all destinations
            console.log('🛒 Matching activities from all destinations...');
            const activityIds = [];
            const selections = {};
            let matchCount = 0;
            
            editData.items.forEach(editItem => {
              const destId = editItem.destination_id;
              const activities = allActivitiesMap[destId];
              
              if (!activities) {
                console.warn(`⚠️ No activities loaded for destination ${destId}`);
                return;
              }
              
              const editDesc = editItem.description?.toLowerCase().trim();
              console.log(`🔍 Looking in destination ${destId}: "${editDesc?.substring(0, 50)}"`);
              
              // Try to find by ID first
              let matched = activities.find(a => parseInt(a.id) === parseInt(editItem.activity_id));
              
              // If not found by ID, try by description
              if (!matched && editDesc) {
                matched = activities.find(a => {
                  const actDesc = a.description?.toLowerCase().trim();
                  return actDesc && (actDesc === editDesc || actDesc.includes(editDesc) || editDesc.includes(actDesc));
                });
              }
              
              if (matched) {
                activityIds.push(matched.id);
                matchCount++;
                
                let paxCount = null;
                if (editItem.type === 'PVT' && editItem.pax_count) {
                  paxCount = editItem.pax_count;
                } else {
                  paxCount = getNextHigherPaxCount(editData.total_pax || 1);
                }
                
                selections[matched.id] = {
                  paxType: editItem.type || 'SIC',
                  paxCount: paxCount,
                  destinationId: destId
                };
                
                console.log(`   ✅ Matched: ${matched.description?.substring(0, 50)}`);
              } else {
                console.warn(`   ❌ No match for: "${editDesc?.substring(0, 50)}"`);
              }
            });
            
            setCart(activityIds);
            setCartSelections(selections);
            cartSetRef.current = true;
            
            console.log(`✅ Matched ${matchCount}/${editData.items.length} activities`);
            
            if (matchCount === 0) {
              setError('No matching activities found. Please select manually.');
            } else {
              setError(`✅ ${matchCount} activities loaded for editing`);
              setTimeout(() => setError(''), 3000);
            }
            
          } catch (err) {
            console.error('❌ Error loading activities:', err);
            setError('Failed to load activities: ' + err.message);
          } finally {
            setLoading(prev => ({ ...prev, activities: false }));
          }
        };
        
        loadAllDestinationsActivities();
      }
      
      setTimeout(() => localStorage.removeItem('edit_quotation'), 30000);
      
    } catch (err) {
      console.error('❌ Error:', err);
      setError('Failed to load edit data');
      editLoadedRef.current = false;
    }
  }
}, []);
// Helper function to set cart from edit data
const setCartFromEditData = (activities, editData) => {
  if (!activities || activities.length === 0) {
    console.warn('No activities available to set cart');
    return;
  }
  
  if (!editData.items || editData.items.length === 0) {
    console.warn('No edit items to set in cart');
    return;
  }
  
  console.log('🛒 Setting cart items from edit data...');
  console.log('Available activity IDs:', activities.map(a => a.id));
  console.log('Edit item IDs:', editData.items.map(i => i.activity_id));
  
  const activityIds = [];
  const selections = {};
  
  editData.items.forEach(editItem => {
    const matchingActivity = activities.find(a => a.id === editItem.activity_id);
    
    if (matchingActivity) {
      activityIds.push(editItem.activity_id);
      
      let paxCount = null;
      if (editItem.type === 'PVT' && editItem.pax_count) {
        paxCount = editItem.pax_count;
      } else {
        paxCount = getNextHigherPaxCount(totalPax);
      }
      
      selections[editItem.activity_id] = {
        paxType: editItem.type || 'SIC',
        paxCount: paxCount,
        destinationId: editItem.destination_id
      };
      
      console.log(`✅ MATCH: Activity ${editItem.activity_id} (${matchingActivity.description?.substring(0, 40)})`);
    } else {
      console.warn(`❌ Activity ${editItem.activity_id} NOT FOUND in loaded activities!`);
      console.log('Available IDs:', activities.map(a => a.id));
      console.log('Looking for ID:', editItem.activity_id);
    }
  });
  
  setCart(activityIds);
  setCartSelections(selections);
  cartSetRef.current = true;
  
  console.log('✅ Final cart set:', activityIds);
  console.log('✅ Final selections:', selections);
  
  if (activityIds.length === 0) {
    setError('Selected activities not found. Please select them manually from the list below.');
  } else if (activityIds.length < editData.items.length) {
    setError(`${activityIds.length} of ${editData.items.length} activities loaded. Please check remaining items.`);
  }
}; // Remove dependencies to avoid loops
// 🔥 SET CART ITEMS - Replace this useEffect

// 🔥 FIXED: Cart setting with proper ID matching
useEffect(() => {
  console.log('🎯 Cart setup check:', {
    isEditMode,
    activitiesCount: currentDestinationActivities.length,
    editItemsCount: editItems.length,
    cartSet: cartSetRef.current
  });
  
  if (isEditMode && currentDestinationActivities.length > 0 && editItems.length > 0 && !cartSetRef.current) {
    console.log('🛒 Setting cart items...');
    console.log('📋 Available activities:', currentDestinationActivities.map(a => ({ id: a.id, desc: a.description?.substring(0, 30) })));
    console.log('📋 Edit items:', editItems.map(i => ({ id: i.activity_id, type: i.type, desc: i.description?.substring(0, 30) })));
    
    const activityIds = [];
    const selections = {};
    let matchCount = 0;
    let notFoundIds = [];
    
    editItems.forEach((editItem, index) => {
      // CRITICAL: Convert both to numbers for comparison
      const targetId = parseInt(editItem.activity_id);
      console.log(`🔍 Looking for activity ID: ${targetId} (${typeof targetId})`);
      
      const matchingActivity = currentDestinationActivities.find(a => {
        const activityId = parseInt(a.id);
        const match = activityId === targetId;
        if (match) {
          console.log(`   ✅ Found match! Activity ID: ${a.id}, Desc: ${a.description?.substring(0, 40)}`);
        }
        return match;
      });
      
      if (matchingActivity) {
        activityIds.push(targetId);
        matchCount++;
        
        let paxCount = null;
        if (editItem.type === 'PVT' && editItem.pax_count) {
          paxCount = editItem.pax_count;
        } else {
          paxCount = getNextHigherPaxCount(totalPax);
        }
        
        selections[targetId] = {
          paxType: editItem.type || 'SIC',
          paxCount: paxCount,
          destinationId: editItem.destination_id
        };
        
        console.log(`   ✅ Added to cart: ${targetId}`);
      } else {
        console.warn(`   ❌ Activity ${targetId} NOT FOUND!`);
        notFoundIds.push(targetId);
      }
    });
    
    setCart(activityIds);
    setCartSelections(selections);
    cartSetRef.current = true;
    
    console.log(`✅ Final result: ${matchCount}/${editItems.length} activities matched`);
    console.log('✅ Cart IDs:', activityIds);
    
    if (matchCount === 0) {
      setError(`No activities found. Available IDs: ${currentDestinationActivities.map(a => a.id).join(', ')}`);
    } else if (matchCount < editItems.length) {
      setError(`${matchCount} of ${editItems.length} activities loaded. Not found: ${notFoundIds.join(', ')}`);
    } else {
      setError(`✅ ${matchCount} activities loaded successfully`);
      setTimeout(() => setError(''), 3000);
    }
  }
}, [isEditMode, currentDestinationActivities, editItems, totalPax]);
  // 🔥 Set cart items AFTER activities are loaded
  useEffect(() => {
    console.log('🎯 Cart setup check:', {
      isEditMode,
      hasActivities: currentDestinationActivities.length > 0,
      hasEditItems: editItems.length > 0,
      cartSet: cartSetRef.current,
      destinationSet: destinationSetRef.current
    });
    
    if (isEditMode && currentDestinationActivities.length > 0 && editItems.length > 0 && !cartSetRef.current) {
      console.log('🛒 Setting cart items...');
      console.log('Available activities count:', currentDestinationActivities.length);
      console.log('Edit items count:', editItems.length);
      
      const activityIds = [];
      const selections = {};
      
      editItems.forEach(editItem => {
        // Find matching activity in loaded activities
        const matchingActivity = currentDestinationActivities.find(a => a.id === editItem.activity_id);
        
        if (matchingActivity) {
          activityIds.push(editItem.activity_id);
          
          let paxCount = null;
          if (editItem.type === 'PVT' && editItem.pax_count) {
            paxCount = editItem.pax_count;
          } else {
            paxCount = getNextHigherPaxCount(totalPax);
          }
          
          selections[editItem.activity_id] = {
            paxType: editItem.type || 'SIC',
            paxCount: paxCount,
            destinationId: editItem.destination_id
          };
          
          console.log(`✅ Added activity ${editItem.activity_id}: ${matchingActivity.description?.substring(0, 40)}`);
        } else {
          console.warn(`⚠️ Activity ${editItem.activity_id} NOT FOUND!`);
          console.log('Available activity IDs:', currentDestinationActivities.map(a => a.id));
        }
      });
      
      setCart(activityIds);
      setCartSelections(selections);
      cartSetRef.current = true;
      
      console.log('✅ Final cart:', activityIds);
      console.log('✅ Final selections:', selections);
      
      if (activityIds.length === 0) {
        setError('Could not load selected activities. Please try selecting them manually.');
      }
    }
  }, [isEditMode, currentDestinationActivities, editItems, totalPax]);

  // Load user data and commission
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setEmail(parsedUser.email || '');
        
        if (parsedUser.client_id) {
          fetchClientCommission(parsedUser.client_id);
        }
      } catch (err) {
        console.error('Error parsing user:', err);
      }
    }
    
    loadCountries();
  }, []);

  const fetchClientCommission = async (clientId) => {
    setLoading(prev => ({ ...prev, commission: true }));
    try {
      const res = await getClientCommission(clientId);
      const commission = parseFloat(res.commission) || 0;
      setClientCommission(commission);
    } catch (err) {
      console.error('Error fetching client commission:', err);
      setClientCommission(0);
    } finally {
      setLoading(prev => ({ ...prev, commission: false }));
    }
  };

  useEffect(() => {
    if (currentDestinationActivities.length > 0) {
      const filtered = currentDestinationActivities.filter(act => 
        act.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredActivities(filtered);
    }
  }, [searchTerm, currentDestinationActivities]);

  const loadCountries = async () => {
    setLoading(prev => ({ ...prev, countries: true }));
    try {
      const res = await getCountries();
      setCountries(res.data || []);
    } catch (err) {
      console.error("Error fetching countries:", err);
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  };

  const loadDestinations = async (countryId) => {
    setLoading(prev => ({ ...prev, destinations: true }));
    try {
      const res = await getDestinations(countryId);
      setDestinations(res.data || []);
      console.log(`✅ Loaded ${res.data?.length || 0} destinations for country ${countryId}`);
    } catch (err) {
      console.error("Error fetching destinations:", err);
    } finally {
      setLoading(prev => ({ ...prev, destinations: false }));
    }
  };

  const loadActivities = async (destinationId) => {
    setLoading(prev => ({ ...prev, activities: true }));
    try {
      if (!allActivities[destinationId]) {
        console.log(`📡 Fetching activities for destination ${destinationId}`);
        const res = await getActivities(destinationId);
        const activitiesData = res.data || [];
        
        console.log(`✅ Loaded ${activitiesData.length} activities`);
        
        setAllActivities(prev => ({
          ...prev,
          [destinationId]: activitiesData
        }));
        
        setCurrentDestinationActivities(activitiesData);
        setFilteredActivities(activitiesData);
      } else {
        console.log(`📦 Using cached activities for destination ${destinationId}`);
        setCurrentDestinationActivities(allActivities[destinationId]);
        setFilteredActivities(allActivities[destinationId]);
      }
    } catch (err) {
      console.error("Error fetching activities:", err);
      setError("Failed to load activities");
    } finally {
      setLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const handleCountrySelect = (countryId, countryName) => {
    console.log(`🌍 Country selected: ${countryName} (${countryId})`);
    setSelectedCountry({ id: countryId, name: countryName });
    loadDestinations(countryId);
    setCurrentPage('booking');
  };

// Dashboard.jsx - Update handleDestinationSelect

const handleDestinationSelect = (destinationId) => {
  console.log(`📍 Destination selected: ${destinationId}`);
  const destination = destinations.find(d => d.id === parseInt(destinationId));
  if (destination) {
    console.log(`✅ Found destination: ${destination.destination_name}`);
    setSelectedDestination(destination);
    
    // Load activities for this destination if not already loaded
    if (allActivities[destination.id]) {
      console.log(`📦 Using cached activities for destination ${destination.id}`);
      setCurrentDestinationActivities(allActivities[destination.id]);
      setFilteredActivities(allActivities[destination.id]);
    } else {
      loadActivities(destination.id);
    }
  } else {
    console.warn(`⚠️ Destination ${destinationId} not found in destinations list`);
  }
};

  const getNextHigherPaxCount = (targetPax) => {
    const availablePax = [2, 4, 6, 8, 10];
    if (targetPax >= 10) return 10;
    for (let pax of availablePax) {
      if (pax >= targetPax) return pax;
    }
    return 10;
  };

  const toggleActivityInCart = (activityId) => {
    let activity = null;
    let activityDestId = null;
    
    Object.keys(allActivities).forEach(destId => {
      const found = allActivities[destId].find(a => a.id === activityId);
      if (found) {
        activity = found;
        activityDestId = destId;
      }
    });
    
    if (!activity) return;
    
    if (cart.includes(activityId)) {
      setCart(cart.filter(id => id !== activityId));
      const newSelections = { ...cartSelections };
      delete newSelections[activityId];
      setCartSelections(newSelections);
    } else {
      const hasSic = activity.sic_price && parseFloat(activity.sic_price) > 0;
      const defaultType = hasSic ? 'SIC' : 'PVT';
      const nextHigherPax = getNextHigherPaxCount(totalPax);
      
      setCart([...cart, activityId]);
      setCartSelections({
        ...cartSelections,
        [activityId]: { 
          paxType: defaultType,
          paxCount: nextHigherPax,
          destinationId: activityDestId
        }
      });
    }
  };

  const updateActivitySelection = (activityId, field, value) => {
    setCartSelections(prev => ({
      ...prev,
      [activityId]: {
        ...prev[activityId],
        [field]: value
      }
    }));
  };

  const getAdultPrice = (activity, paxType, paxCount) => {
    if (paxType === 'SIC') {
      return parseFloat(activity.sic_price) || 0;
    } else {
      const price = activity[`pvt_${paxCount}pax_price`];
      return parseFloat(price) || 0;
    }
  };

  const getChildPrice = (activity, paxType, paxCount) => {
    if (paxType === 'SIC') {
      const childPrice = parseFloat(activity.sic_child_price);
      return (childPrice && childPrice > 0) ? childPrice : 0;
    } else {
      const childPrice = parseFloat(activity.pvt_child_price);
      return (childPrice && childPrice > 0) ? childPrice : 0;
    }
  };

  const isSicAvailable = (activity) => {
    return activity.sic_price !== null && parseFloat(activity.sic_price) > 0;
  };

  const isPvtAvailable = (activity) => {
    return activity.pvt_2pax_price || 
           activity.pvt_4pax_price || 
           activity.pvt_6pax_price || 
           activity.pvt_8pax_price || 
           activity.pvt_10pax_price;
  };

  const calculateQuotation = () => {
    if (cart.length === 0) {
      setError('Please select at least one activity');
      return;
    }

    if (!guestName || guestName.trim() === '') {
      setError('Please enter guest name');
      return;
    }

    if (!email || email.trim() === '') {
      setError('Please enter email address');
      return;
    }

    if (!checkInDate) {
      setError('Please select check-in date');
      return;
    }

    let adultTotal = 0;
    let childTotal = 0;
    let items = [];
    
    const adults = totalPax - childCount;
    
    cart.forEach(activityId => {
      let activity = null;
      Object.keys(allActivities).forEach(destId => {
        const found = allActivities[destId].find(a => a.id === activityId);
        if (found) activity = found;
      });
      
      const selection = cartSelections[activityId];
      if (!activity || !selection) return;
      
      const adultPrice = getAdultPrice(activity, selection.paxType, selection.paxCount);
      const childPrice = getChildPrice(activity, selection.paxType, selection.paxCount);
      
      adultTotal += adultPrice;
      if (childCount > 0) {
        childTotal += childPrice;
      }
      
      items.push({
        activity_id: activity.id,
        destination_id: selection.destinationId,
        description: activity.description,
        type: selection.paxType,
        paxCount: selection.paxCount,
        adultPrice,
        childPrice
      });
    });
    
    const baseTotalPerPerson = adultTotal + childTotal;
    const clientCommissionAmount = (baseTotalPerPerson * clientCommission) / 100;
    const totalPerPersonWithClientCommission = baseTotalPerPerson + clientCommissionAmount;
    
    setAgentProfit(0);
    
    setQuotationData({
      baseTotalPerPerson: baseTotalPerPerson,
      clientCommissionPercent: clientCommission,
      clientCommissionAmount: clientCommissionAmount,
      totalPerPerson: totalPerPersonWithClientCommission,
      agentProfit: 0,
      finalTotal: totalPerPersonWithClientCommission,
      adultPerPerson: adultTotal,
      childPerPerson: childTotal,
      adults,
      children: childCount,
      items
    });
    
    setModalEmail(email);
    setShowQuotationModal(true);
  };

  const updateAgentProfit = (profitValue) => {
    const profit = parseFloat(profitValue) || 0;
    setAgentProfit(profit);
    
    setQuotationData(prev => ({
      ...prev,
      agentProfit: profit,
      finalTotal: prev.totalPerPerson + profit
    }));
  };

  const sendQuotationEmail = async () => {
    if (!guestName || guestName.trim() === '') {
      setError('Guest name is required');
      setShowQuotationModal(false);
      return;
    }
    
    if (!modalEmail || modalEmail.trim() === '') {
      setError('Please enter customer email');
      return;
    }
    
    if (!checkInDate) {
      setError('Please select check-in date');
      setShowQuotationModal(false);
      return;
    }

    setSendingEmail(true);
    
    try {
      const data = {
        client_id: user?.client_id,
        guest_name: guestName.trim(),
        email: modalEmail.trim(),
        check_in_date: checkInDate,
        total_pax: totalPax,
        children_count: childCount,
        items: quotationData.items.map(item => ({
          activity_id: item.activity_id,
          destination_id: item.destination_id,
          description: item.description,
          type: item.type,
          pax_count: item.paxCount,
          adult_price: item.adultPrice,
          child_price: item.childPrice
        })),
        totals: {
          adults: quotationData.adults,
          children: quotationData.children,
          adult_per_person: quotationData.adultPerPerson,
          child_per_person: quotationData.childPerPerson,
          base_total_per_person: quotationData.baseTotalPerPerson,
          client_commission_percent: quotationData.clientCommissionPercent,
          client_commission_amount: quotationData.clientCommissionAmount,
          total_per_person: quotationData.totalPerPerson,
          agent_profit: quotationData.agentProfit,
          final_total: quotationData.finalTotal
        }
      };
      
      let response;
      
      if (isEditMode && editBookingId) {
        console.log('✏️ Updating existing quotation:', editBookingId);
        response = await updateQuotationAndSendEmail(editBookingId, data);
        if (response.status) {
          alert(`✅ Quotation updated and email sent successfully to ${modalEmail}`);
          resetForm();
          setIsEditMode(false);
          setEditBookingId(null);
          setEditItems([]);
          cartSetRef.current = false;
          editLoadedRef.current = false;
          destinationSetRef.current = false;
        }
      } else {
        console.log('📝 Creating new quotation');
        response = await createBookingAndSendQuotation(data);
        if (response.status) {
          alert(`✅ New quotation created and sent successfully to ${modalEmail}`);
          resetForm();
        }
      }
      
      if (!response.status) {
        setError(response.message || 'Failed to process');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to process quotation');
    } finally {
      setSendingEmail(false);
      setShowQuotationModal(false);
    }
  };

  const resetForm = () => {
    setCart([]);
    setCartSelections({});
    setSelectedDestination(null);
    setCurrentDestinationActivities([]);
    setFilteredActivities([]);
    setGuestName('');
    setEmail(user?.email || '');
    setCheckInDate('');
    setTotalPax(1);
    setChildCount(0);
    setIsEditMode(false);
    setEditBookingId(null);
    setEditItems([]);
    cartSetRef.current = false;
    editLoadedRef.current = false;
    destinationSetRef.current = false;
  };

  const removeFromCart = (activityId) => {
    setCart(cart.filter(id => id !== activityId));
    const newSelections = { ...cartSelections };
    delete newSelections[activityId];
    setCartSelections(newSelections);
  };

  const handleNavigation = (url) => {
    if (url) {
      navigate(url);
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const isActive = (url) => location.pathname === url;

  const ActivityCard = ({ activity }) => {
    const isSelected = cart.includes(activity.id);
    const selection = cartSelections[activity.id];
    const sicAvailable = isSicAvailable(activity);
    const pvtAvailable = isPvtAvailable(activity);
    const nextHigherPax = getNextHigherPaxCount(totalPax);
    const hasChildPrice = (activity.sic_child_price && parseFloat(activity.sic_child_price) > 0) ||
                          (activity.pvt_child_price && parseFloat(activity.pvt_child_price) > 0);

    return (
      <div className={`bg-white rounded-lg border transition-all ${
        isSelected ? 'border-amber-500 bg-amber-50/20' : 'border-gray-200 hover:border-amber-300'
      }`}>
        <div className="p-3">
          <div className="flex items-start gap-2">
            <div className="pt-0.5">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleActivityInCart(activity.id)}
                className="w-4 h-4 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
              />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm text-gray-800 line-clamp-2 text-left">{activity.description}</p>
              {isSelected && (
                <div className="mt-2 space-y-1 text-left">
                  <div className="flex items-center gap-3 flex-wrap">
                    {sicAvailable && (
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`type-${activity.id}`}
                          checked={selection?.paxType === 'SIC'}
                          onChange={() => updateActivitySelection(activity.id, 'paxType', 'SIC')}
                          className="w-3 h-3 text-amber-500"
                        />
                        <span className="text-xs text-gray-600">SIC</span>
                      </label>
                    )}
                    {pvtAvailable && (
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`type-${activity.id}`}
                          checked={selection?.paxType === 'PVT'}
                          onChange={() => {
                            updateActivitySelection(activity.id, 'paxType', 'PVT');
                            updateActivitySelection(activity.id, 'paxCount', nextHigherPax);
                          }}
                          className="w-3 h-3 text-amber-500"
                        />
                        <span className="text-xs text-gray-600">PVT</span>
                      </label>
                    )}
                  </div>
                  {childCount > 0 && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      {hasChildPrice ? 'Child price from DB' : 'No child price in DB → 0'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const QuotationModal = () => {
    if (!quotationData) return null;
    
    const basePerPerson = quotationData.totalPerPerson;
    const finalPerPerson = basePerPerson + agentProfit;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-md w-full">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-bold text-gray-800">
              {isEditMode ? 'Update Quotation' : 'Quotation Details'}
            </h3>
            <button
              onClick={() => setShowQuotationModal(false)}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Rate Per Person</label>
              <div className="bg-gray-100 p-4 rounded-lg text-center">
                <span className="text-2xl font-bold text-amber-600">{basePerPerson.toFixed(2)}</span>
                <span className="text-gray-500 ml-1">THB</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Agent Profit (Optional)</label>
              <div className="flex items-center">
                <span className="bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg px-3 py-2 text-gray-600">+</span>
                <input
                  type="number"
                  value={agentProfit}
                  onChange={(e) => updateAgentProfit(e.target.value)}
                  step="10"
                  min="0"
                  className="w-full p-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  placeholder="0"
                />
                <span className="ml-2 text-gray-500">THB</span>
              </div>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Total Rate Per Person</p>
                <p className="text-3xl font-bold text-amber-600">{finalPerPerson.toFixed(2)} <span className="text-lg">THB</span></p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Customer Email *</label>
              <input
                type="email"
                value={modalEmail}
                onChange={(e) => setModalEmail(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                placeholder="customer@email.com"
                autoFocus
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-blue-600">
                <strong>Guest Name:</strong> {guestName || 'Not provided'}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                <strong>Check-in Date:</strong> {checkInDate || 'Not provided'}
              </p>
              {isEditMode && (
                <p className="text-xs text-amber-600 mt-1">
                  <strong>Mode:</strong> Updating existing quotation
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={sendQuotationEmail}
                disabled={sendingEmail}
                className="flex-1 bg-green-500 text-white py-2.5 rounded-lg font-medium hover:bg-green-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sendingEmail ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {sendingEmail ? 'Sending...' : (isEditMode ? 'Update & Send' : 'Send Quotation')}
              </button>
              <button
                onClick={() => setShowQuotationModal(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // COUNTRIES PAGE
  if (currentPage === 'countries') {
    return (
      <div className="min-h-screen bg-gray-50">
      

        <main className="max-w-7xl mx-auto px-4 py-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Select Country</h2>
          {loading.countries ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-amber-500" size={40} /></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {countries.map(country => (
                <button
                  key={country.id}
                  onClick={() => handleCountrySelect(country.id, country.country_name)}
                  className="group p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all text-center border border-gray-100 hover:border-amber-200"
                >
                  <Globe className="w-8 h-8 text-amber-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium text-gray-700">{country.country_name}</span>
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // BOOKING PAGE
  return (
    <div className="min-h-screen bg-gray-50">
      

      <main className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
            <Info size={16} />
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              className="p-2 text-sm border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              value={selectedDestination?.id || ''}
              onChange={(e) => handleDestinationSelect(e.target.value)}
            >
              <option value="">Select Destination</option>
              {destinations.map(d => (
                <option key={d.id} value={d.id}>{d.destination_name}</option>
              ))}
            </select>
            <input
              type="date"
              className="p-2 text-sm border rounded-lg focus:ring-2 focus:ring-amber-500"
              value={checkInDate}
              onChange={(e) => setCheckInDate(e.target.value)}
              placeholder="Check-in Date"
            />
            <input
              type="number"
              placeholder="Total PAX"
              className="p-2 text-sm border rounded-lg"
              value={totalPax}
              onChange={(e) => setTotalPax(parseInt(e.target.value) || 1)}
              min="1"
            />
            <input
              type="number"
              placeholder="Children"
              className="p-2 text-sm border rounded-lg"
              value={childCount}
              onChange={(e) => setChildCount(Math.min(parseInt(e.target.value) || 0, totalPax))}
              min="0"
              max={totalPax}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <input
              type="text"
              placeholder="Guest Name *"
              className="p-2 text-sm border rounded-lg focus:ring-2 focus:ring-amber-500"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
            />
            <input
              type="email"
              placeholder="Email Address *"
              className="p-2 text-sm border rounded-lg focus:ring-2 focus:ring-amber-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-[70%]">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search activities..."
                  className="w-full pl-10 pr-3 py-2 text-sm border rounded-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading.activities ? (
              <div className="bg-white rounded-lg p-8 text-center">
                <Loader2 className="animate-spin text-amber-500 mx-auto" size={32} />
                <p className="text-sm text-gray-500 mt-2">Loading activities...</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {filteredActivities.length > 0 ? (
                  filteredActivities.map(activity => <ActivityCard key={activity.id} activity={activity} />)
                ) : currentDestinationActivities.length === 0 ? (
                  <div className="bg-white rounded-lg p-8 text-center">
                    <Info size={32} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Select a destination to view activities</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-8 text-center">
                    <Info size={32} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No matching activities found</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:w-[30%]">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-24">
              <h3 className="font-bold mb-3">Selected ({cart.length})</h3>
              <div className="space-y-2 max-h-[500px] overflow-y-auto mb-4">
                {cart.length > 0 ? (
                  cart.map(activityId => {
                    let activity = null;
                    let destName = '';
                    Object.keys(allActivities).forEach(destId => {
                      const found = allActivities[destId].find(a => a.id === activityId);
                      if (found) { 
                        activity = found; 
                        const dest = destinations.find(d => d.id === parseInt(destId));
                        destName = dest?.destination_name || '';
                      }
                    });
                    if (!activity) return null;
                    const selection = cartSelections[activityId];
                    return (
                      <div key={activityId} className="flex justify-between items-start p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-800 truncate">{activity.description?.substring(0, 50)}</p>
                          {selection && (
                            <p className="text-[10px] text-amber-600 mt-0.5">
                              {selection.paxType} {selection.paxType === 'PVT' ? `(${selection.paxCount}P)` : ''}
                            </p>
                          )}
                        </div>
                        <button onClick={() => removeFromCart(activityId)} className="text-red-400 ml-2">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-400 py-4">No items selected</p>
                )}
              </div>
              {cart.length > 0 && (
                <button onClick={calculateQuotation} className="w-full bg-amber-500 text-white py-2 rounded-lg font-medium hover:bg-amber-600">
                  Get Quotation
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      {showQuotationModal && <QuotationModal />}
    </div>
  );
};

export default Dashboard;